import { t } from "../../validation/validator";
import { stripeMinimumCouponSchema } from "./stripeCoupon.schemas";
import { stripeDiscountSchema } from "./stripeDiscount.schemas";

// ─── V1 Invoice ──────────────────────────────────────────────────────────────

const invoiceDescriptionSchema = t.type({
  type: t.union([
    t.literal("subscription_create"),
    t.literal("subscription_update"),
    t.literal("subscription_cycle"),
  ]),
  interval: t.union([t.literal("month"), t.literal("year")]),
  previous_interval: t.union([t.literal("month"), t.literal("year")]),
  quantity: t.number,
  quantity_changed: t.union([t.number, t.undefined]),
});

export const stripeInvoiceBaseFields = {
  amount_due: t.number,
  amount_paid: t.number,
  amount_remaining: t.number,
  currency: t.string,
  status: t.union([
    t.literal("draft"),
    t.literal("open"),
    t.literal("paid"),
    t.literal("uncollectible"),
    t.literal("void"),
    t.null,
  ]),
  created: t.number,
  period_start: t.number,
  period_end: t.number,
  due_date: t.union([t.number, t.null]),
  billing_reason: t.union([
    t.literal("subscription_create"),
    t.literal("subscription_cycle"),
    t.literal("subscription_update"),
    t.literal("subscription"),
    t.literal("manual"),
    t.literal("upcoming"),
    t.null,
  ]),
  invoice_pdf: t.union([t.string, t.null]),
  hosted_invoice_url: t.union([t.string, t.null]),
  description: t.union([invoiceDescriptionSchema, t.null]),
  lines: t.type({
    data: t.array(
      t.type({
        id: t.string,
        amount: t.number,
        currency: t.string,
        description: t.union([t.string, t.null]),
        quantity: t.union([t.number, t.null]),
        plan: t.type({
          id: t.string,
          interval: t.union([t.literal("month"), t.literal("year")]),
          interval_count: t.number,
        }),
        proration: t.boolean,
        unit_amount: t.number,
        period: t.type({
          start: t.number,
          end: t.number,
        }),
        type: t.union([t.literal("subscription"), t.literal("invoiceitem")]),
        status: t.union([
          t.literal("incomplete"),
          t.literal("incomplete_expired"),
          t.literal("trialing"),
          t.literal("active"),
          t.literal("past_due"),
          t.literal("canceled"),
          t.literal("unpaid"),
          t.literal("paused"),
        ]),
      })
    ),
  }),
  total: t.number,
  subtotal: t.number,
  starting_balance: t.number,
  ending_balance: t.number,
  discounts: t.array(t.union([stripeDiscountSchema, t.string])),
  total_discount_amounts: t.array(
    t.type({
      amount: t.number,
    })
  ),
};

export const stripeInvoiceSchema = t.type({
  ...stripeInvoiceBaseFields,
  id: t.string,
  paid: t.boolean,
  payment_intent: t.union([
    t.type({
      client_secret: t.string,
    }),
    t.null,
  ]),
});
export type StripeInvoice = t.TypeOf<typeof stripeInvoiceSchema>;

export const stripeInvoicePreviewSchema = t.intersection([
  t.type(stripeInvoiceBaseFields),
  t.partial({
    id: t.string,
  }),
]);
export type StripeInvoicePreview = t.TypeOf<typeof stripeInvoicePreviewSchema>;

export const StripeInvoiceStatus = {
  DRAFT: "draft",
  OPEN: "open",
  PAID: "paid",
  UNCOLLECTIBLE: "uncollectible",
  VOID: "void",
} as const;
export type StripeInvoiceStatusValue = (typeof StripeInvoiceStatus)[keyof typeof StripeInvoiceStatus];

// Simplified invoice info for incomplete subscriptions (contains payment link)
export const incompleteInvoiceInfoSchema = t.type({
  id: t.string,
  amount_due: t.number,
  currency: t.string,
  status: t.union([
    t.literal("draft"),
    t.literal("open"),
    t.literal("paid"),
    t.literal("uncollectible"),
    t.literal("void"),
  ]),
  hosted_invoice_url: t.union([t.string, t.null]),
  invoice_pdf: t.union([t.string, t.null]),
});

// ─── Invoice (getInvoices, getUpcomingInvoice) ────────────────────────────────

const stripeInvoiceDescriptionSchema = t.type({
  type: t.union([
    t.literal("subscription_create"),
    t.literal("subscription_update"),
    t.literal("subscription_cycle"),
    t.literal("seat_addition"),
  ]),
  interval: t.union([t.literal("month"), t.literal("year"), t.null]),
  quantity: t.number,
  quantity_changed: t.union([t.number, t.undefined]),
  previous_interval: t.union([t.literal("month"), t.literal("year"), t.null, t.undefined]),
});

export const stripeInvoiceSummarySchema = t.type({
  id: t.union([t.string, t.undefined]),
  amount_due: t.number,
  amount_paid: t.number,
  amount_remaining: t.number,
  currency: t.string,
  status: t.union([t.string, t.null, t.undefined]),
  created: t.number,
  period_start: t.number,
  period_end: t.number,
  due_date: t.union([t.number, t.null, t.undefined]),
  paid: t.boolean,
  billing_reason: t.union([t.string, t.null, t.undefined]),
  invoice_pdf: t.union([t.string, t.null, t.undefined]),
  hosted_invoice_url: t.union([t.string, t.null, t.undefined]),
  description: t.union([stripeInvoiceDescriptionSchema, t.null]),
  lines: t.array(t.unknown),
});
export type StripeInvoiceSummary = t.TypeOf<typeof stripeInvoiceSummarySchema>;

export const stripeInvoiceListResponseSchema = t.type({
  has_more: t.boolean,
  data: t.array(stripeInvoiceSummarySchema),
});
export type StripeInvoiceListResponse = t.TypeOf<typeof stripeInvoiceListResponseSchema>;

export const stripeGetInvoicesRequestSchema = t.intersection([
  t.type({
    user_id: t.string,
  }),
  t.partial({
    limit: t.number,
    starting_after: t.string,
    ending_before: t.string,
  }),
]);
export type StripeGetInvoicesRequest = t.TypeOf<typeof stripeGetInvoicesRequestSchema>;

export const stripePreviewLineItemSchema = t.type({
  description: t.union([t.string, t.null]),
  amount: t.number,
  quantity: t.union([t.number, t.null]),
  currency: t.string,
});
export type StripePreviewLineItem = t.TypeOf<typeof stripePreviewLineItemSchema>;

export const stripeUpcomingInvoiceResponseSchema = t.intersection([
  t.type({
    type: t.union([t.literal("custom_invoice"), t.literal("standard_invoice")]),
    amount_due: t.number,
    amount_due_now: t.number,
    new_recurring_total: t.number,
    subtotal: t.number,
    total: t.number,
    currency: t.string,
    period_start: t.number,
    period_end: t.number,
    lines: t.array(stripePreviewLineItemSchema),
    coupon: t.union([stripeMinimumCouponSchema, t.null]),
  }),
  t.partial({
    invoice: stripeInvoicePreviewSchema,
  }),
]);
export type StripeUpcomingInvoiceResponse = t.TypeOf<typeof stripeUpcomingInvoiceResponseSchema>;

export const stripeGetUpcomingInvoiceRequestSchema = t.intersection([
  t.type({
    user_id: t.string,
    subscription_id: t.string,
    current_sub_item_id: t.string,
  }),
  t.partial({
    new_price_id: t.string,
    quantity: t.number,
    promo_id: t.string,
    billing_cycle_anchor: t.union([t.literal("now"), t.literal("unchanged")]),
  }),
]);
export type StripeGetUpcomingInvoiceRequest = t.TypeOf<typeof stripeGetUpcomingInvoiceRequestSchema>;
